export type NotificationChannelState =
  | 'enabled'
  | 'disabled_by_user'
  | 'required'
  | 'unavailable_platform'
  | 'unavailable_policy'
  | 'degraded';

export type NotificationChannelDefinition = {
  status: 'enabled' | 'unavailable_platform' | 'unavailable_policy' | 'degraded';
  supported: boolean;
  label: string;
  description: string;
};

export type NotificationEventChannel = {
  label: string;
  state: NotificationChannelState;
  enabled: boolean;
  editable: boolean;
};

export type NotificationEventFamily = {
  key: string;
  label: string;
  description: string;
  mandatory: boolean;
  channels: {
    in_app: NotificationEventChannel;
    email: NotificationEventChannel;
    sms: NotificationEventChannel;
    push: NotificationEventChannel;
  };
};

export type NotificationPreferenceResponse = {
  channels: {
    in_app: NotificationChannelDefinition;
    email: NotificationChannelDefinition;
    sms: NotificationChannelDefinition;
    push: NotificationChannelDefinition;
  };
  event_families: NotificationEventFamily[];
  global_settings: {
    digest_mode: string;
    digest_timezone: string | null;
    quiet_hours_enabled: boolean;
    quiet_hours_start_minute: number | null;
    quiet_hours_end_minute: number | null;
    quiet_hours_timezone: string | null;
    schedule_reminder_enabled: boolean;
    schedule_reminder_minutes_before: number | null;
  };
  policy: {
    has_mandatory_exceptions: boolean;
    mandatory_event_families: string[];
  };
  delivery_status: {
    fallback_channel_for_mandatory_events: string;
    sender_fallback_rule: string;
  };
};

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

const readJson = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const message = response.status === 422
      ? 'Please review the notification settings and try again.'
      : `Notification preferences request failed (${response.status}).`;
    throw new Error(message);
  }

  return response.json() as Promise<T>;
};

export const fetchNotificationPreferences = async (): Promise<NotificationPreferenceResponse> =>
  readJson<NotificationPreferenceResponse>(
    await fetch('/api/v1/secure/profile/notification-preferences', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );

export const saveNotificationPreferences = async (payload: {
  event_families: Array<{
    key: string;
    channels: {
      in_app: boolean;
    };
  }>;
}): Promise<NotificationPreferenceResponse> =>
  readJson<NotificationPreferenceResponse>(
    await fetch('/api/v1/secure/profile/notification-preferences', {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(payload),
    })
  );
